import { bool, cleanEnv, str } from 'envalid';
import { featureRegistryService } from './FeatureRegistryService';

/**
 * Expand $VAR/${VAR} to environment variables.
 * Note: Tilde (~) expansion is NOT supported; users must use absolute paths or env vars like $HOME.
 */
function expandPath(path: string | undefined): string | undefined {
  if (!path) return path;

  // Expand environment variables ($VAR or ${VAR})
  return path.replace(/\$([a-zA-Z_][a-zA-Z0-9_]*)|\${([a-zA-Z_][a-zA-Z0-9_]*)}/g, (_, p1, p2) => {
    const varName = p1 || p2;
    return process.env[varName] ?? '';
  });
}

export const coreEnvSchema = {
  CONTENT_ROOT: str({
    desc: 'Root directory path for content files',
    example: '../inovisum-content/inovisum',
  }),
  AUTH_URL: str({
    desc: 'Base URL for authentication callbacks',
    example: 'http://localhost:4200',
  }),
  AUTH_SECRET: str({
    desc: 'Secret key for signing authentication tokens',
    example: 'your-secret-key-here',
  }),
  DATABASE_URL: str({
    desc: 'Database connection string',
    example: 'file:./dev.db',
  }),
  POOLED_DATABASE_URL: str({
    default: undefined,
    desc: 'Optional pooled database URL for runtime Prisma Client. If set, overrides DATABASE_URL for the app runtime only.',
    example: 'postgresql://user:pass@ep-xxxxx-pooler.neon.tech/neondb?sslmode=require&pgbouncer=true',
  }),
  DEBUG_LOG_DB_URL: bool({
    default: false,
    desc: 'When true, logs masked DATABASE_URL/POOLED_DATABASE_URL and performs a connectivity check at runtime.',
  }),
  PLAYWRIGHT_TESTING: bool({
    default: false,
    desc: 'When true, enables Playwright test mode for server-side helpers (used by scripts/run-ui.ts).',
  }),
  SUPER_ADMIN_EMAIL: str({
    desc: 'Email address of the super admin user',
    example: 'admin@example.com',
  }),
  READ_ONLY: bool({
    default: false,
    desc: 'Enable read-only mode (disables all write operations). Defaults to false if not set.',
  }),
  KROKI_BASE_URL: str({
    desc: 'Base URL for Kroki diagram rendering. REQUIRED. If unset, diagrams will not render and URLs will be empty.',
    example: 'https://diagrams.example.com',
  }),
  KROKI_FALLBACK_URL: str({
    default: 'https://kroki.io',
    desc: 'Fallback URL for Kroki if the primary URL fails. Defaults to official Kroki service.',
    example: 'https://kroki.io',
  }),
  PRIMARY_ORG_SLUG: str({
    default: undefined,
    desc: 'Primary org slug treated as platform-owned; affects default routing like /v/:slug (optional)',
  }),
  OAUTH_GOOGLE_ID: str({
    default: undefined,
    desc: 'Google OAuth client ID (requires OAUTH_GOOGLE_SECRET)',
  }),
  OAUTH_GOOGLE_SECRET: str({
    default: undefined,
    desc: 'Google OAuth client secret (requires OAUTH_GOOGLE_ID)',
  }),
  OAUTH_AZURE_ID: str({
    default: undefined,
    desc: 'Azure AD OAuth client ID (requires OAUTH_AZURE_SECRET and OAUTH_AZURE_TENANT)',
  }),
  OAUTH_AZURE_SECRET: str({
    default: undefined,
    desc: 'Azure AD OAuth client secret (requires OAUTH_AZURE_ID and OAUTH_AZURE_TENANT)',
  }),
  OAUTH_AZURE_TENANT: str({
    default: undefined,
    desc: 'Azure AD tenant ID (requires OAUTH_AZURE_ID and OAUTH_AZURE_SECRET)',
  }),
  NODE_ENV: str({
    choices: ['development', 'production', 'test'],
    default: 'development',
    desc: 'Node.js environment mode. Defaults to development if not set.',
  }),
  PUPPETEER_EXECUTABLE_PATH: str({
    default: undefined,
    desc: 'Optional path to system Chromium for Puppeteer (container/Nixpacks)',
    example: '/usr/bin/chromium',
  }),
};

export class CoreEnvironmentService {
  private featureSchemas: Map<string, Record<string, any>> = new Map();
  private validatedEnv: ReturnType<typeof cleanEnv> | null = null;

  public registerFeatureEnvSchema(featureName: string, schema: Record<string, any>): void {
    const existingSchema = this.featureSchemas.get(featureName);
    if (existingSchema) {
      if (existingSchema === schema) {
        // Idempotent re-registration with the same schema; no-op.
        return;
      }
      throw new Error(
        `Feature "${featureName}" attempted to register a different environment schema after initial registration.`,
      );
    }
    this.featureSchemas.set(featureName, schema);
  }

  public validateEnv(): ReturnType<typeof cleanEnv> {
    if (this.validatedEnv) {
      return this.validatedEnv;
    }

    // Allow build-time or explicit skipping of environment validation.
    // This prevents Next.js "collecting page data" phase from failing when
    // runtime-only secrets are not present during builds.
    const skipValidation =
      process.env.SKIP_ENV_VALIDATION === '1' ||
      process.env.SKIP_ENV_VALIDATION === 'true';

    if (skipValidation) {
      // Do not perform schema validation; use raw process.env instead.
      // Cast is intentional to satisfy typing without enforcing presence.
      this.validatedEnv = process.env as unknown as ReturnType<typeof cleanEnv>;
      return this.validatedEnv;
    }

    const featureModules = featureRegistryService.getFeatureModules();
    for (const feature of featureModules) {
      if (feature.envSchema) {
        this.registerFeatureEnvSchema(feature.name, feature.envSchema);
      }
    }

    const combinedSchema = {
      ...coreEnvSchema,
      ...Array.from(this.featureSchemas.values()).reduce((acc, schema) => ({ ...acc, ...schema }), {}),
    };

    const validated = cleanEnv(process.env, combinedSchema, {
      reporter: ({ errors }) => {
        if (Object.keys(errors).length > 0) {
          const errorMessages = Object.entries(errors)
            .map(([key, err]) => `  - ${key}: ${err}`)
            .join('\n');
          const message = `❌ Environment validation failed:

${errorMessages}

Please ensure all required environment variables are set correctly.`;
          throw new Error(message);
        }
      },
    });

    // Normalize paths: expand ~ and environment variables
    // This ensures all code reading CONTENT_ROOT gets the expanded absolute path.
    this.validatedEnv = {
      ...validated,
      CONTENT_ROOT: expandPath(validated.CONTENT_ROOT) ?? validated.CONTENT_ROOT,
    } as typeof validated;

    this.ensureOAuthProvidersAreValid(this.validatedEnv);

    // Run bootstrap AFTER validatedEnv is set to avoid circular dependency:
    // bootstrap() -> getEnv() -> validateEnv() -> ensureBootstrapped() -> bootstrap()
    // Now when bootstrap calls getEnv(), validatedEnv is already cached.
    featureRegistryService.ensureBootstrapped();

    return this.validatedEnv;
  }

  public getEnv(): ReturnType<typeof cleanEnv> {
    if (!this.validatedEnv) {
      this.validateEnv();
    }
    return this.validatedEnv!;
  }

  public resetValidation(): void {
    this.featureSchemas.clear();
    this.validatedEnv = null;
  }

  private ensureOAuthProvidersAreValid(env: Record<string, any>): void {
    const hasGoogleId = env.OAUTH_GOOGLE_ID !== undefined;
    const hasGoogleSecret = env.OAUTH_GOOGLE_SECRET !== undefined;
    if (hasGoogleId !== hasGoogleSecret) {
      throw new Error(
        'Invalid OAuth configuration: Both OAUTH_GOOGLE_ID and OAUTH_GOOGLE_SECRET must be set together, or neither should be set.',
      );
    }

    const hasAzureId = env.OAUTH_AZURE_ID !== undefined;
    const hasAzureSecret = env.OAUTH_AZURE_SECRET !== undefined;
    const hasAzureTenant = env.OAUTH_AZURE_TENANT !== undefined;
    const azureValues = [hasAzureId, hasAzureSecret, hasAzureTenant];
    const azureDefinedCount = azureValues.filter(Boolean).length;

    if (azureDefinedCount > 0 && azureDefinedCount < 3) {
      throw new Error(
        'Invalid OAuth configuration: OAUTH_AZURE_ID, OAUTH_AZURE_SECRET, and OAUTH_AZURE_TENANT must all be set together, or none should be set.',
      );
    }
  }

}

export const coreEnvironmentService = new CoreEnvironmentService();

export function registerFeatureEnvSchema(featureName: string, schema: Record<string, any>): void {
  coreEnvironmentService.registerFeatureEnvSchema(featureName, schema);
}

export function validateEnv(): ReturnType<typeof cleanEnv> {
  return coreEnvironmentService.validateEnv();
}

export function getEnv(): ReturnType<typeof cleanEnv> {
  return coreEnvironmentService.getEnv();
}

export function resetEnvValidation(): void {
  coreEnvironmentService.resetValidation();
}
